import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { GEOFENCE_SHAPE } from '../../../constants/bucket.contants'
import { useCircle } from '../../../hooks/use-circle'
import { usePolygon } from '../../../hooks/use-polygon'
import { PositionType, SingleAssetGeofencesType } from '../../../types/geofence.type'
import { CircleMapper } from './circle-mapper'
import { PolygonMapper } from './polygon-mapper'
import { getImgSrc } from '../../../helpers/getImageSrc'
import { defaultMapCenter } from '../../../constants/map.constants'
import { useDarkMode } from '../../../hooks/use-darkmode'
import { darkModeStyle } from '../../../constants/navigation.constants';
interface Props {
    assetCoordinates?: google.maps.LatLng
    geofenceType: GEOFENCE_SHAPE
    radius: number
    geofenceCoordinates: PositionType[]
    setGeofenceCoordinates: (position: PositionType[]) => void
    selectedGeofence?: SingleAssetGeofencesType | null
}

export interface GeofenceMapRef{
    removeLastPoint: () => void
}

const GeofenceMap = forwardRef((props: Props,ref:React.ForwardedRef<GeofenceMapRef>) => {
    const { darkMode } = useDarkMode();
    const {
        geofenceCoordinates,
        geofenceType,
        setGeofenceCoordinates,
        radius: defaultRadius,
    } = props
    const [assetData, setAssetData] = useState<any>()
    useEffect(() => {
        const sessionAssetData = sessionStorage.getItem('assetData');
        if (sessionAssetData) {
            setAssetData(JSON.parse(sessionAssetData))
        }
    }, [])
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY as string,
    })
    const { addPoint, points, removeLastPoint, updatePoint } =
        usePolygon(geofenceCoordinates)
    const { center, radius, setCenter } = useCircle(
        {
            latitude: geofenceCoordinates?.[0]?.latitude,
            longitude: geofenceCoordinates?.[0]?.longitude,
        },
        defaultRadius
    )
    const onMapClick = useCallback(
        (mouseEvent: google.maps.MapMouseEvent) => {
            if (props?.geofenceType == GEOFENCE_SHAPE.POLYGON) {
                const latitude = mouseEvent.latLng?.lat?.()
                const longitude = mouseEvent.latLng?.lng?.()

                if (latitude && longitude) {
                    addPoint({ latitude, longitude })
                }
            } else {
                if (mouseEvent?.latLng) {
                    setCenter({
                        latitude: mouseEvent.latLng.lat(),
                        longitude: mouseEvent.latLng.lng(),
                    })
                }
            }
        },
        [points]
    )

    useEffect(() => {
        if (props.geofenceType == GEOFENCE_SHAPE.POLYGON) {
            setGeofenceCoordinates(points)
        } else if (props?.geofenceType == GEOFENCE_SHAPE.CIRCLE) {
            setGeofenceCoordinates([{ order: 0, ...center } as PositionType])
        }
    }, [points, center])

    useImperativeHandle(ref, () => ({
        removeLastPoint: () => {
            removeLastPoint()
        },
    }));

    const mapStyles = darkMode ? darkModeStyle : [];
    return isLoaded ? (
        <GoogleMap
            mapContainerStyle={{
                width: '100%',
                height: '100%',
            }}
            center={props?.assetCoordinates ?? defaultMapCenter}
            zoom={props?.assetCoordinates ? 15 : 5}
            onClick={onMapClick}
            options={{styles:mapStyles}}
        >
            {props?.assetCoordinates && assetData && (
                <MarkerF
                    position={props?.assetCoordinates}
                    icon={{
                        url: getImgSrc(assetData.tag, true),
                        scaledSize: new google.maps.Size(25, 30)
                    }}
                />
            )}
            {geofenceType == GEOFENCE_SHAPE.POLYGON && (
                <PolygonMapper
                    coordinates={points}
                    isEdit
                    updatePoint={updatePoint}
                />
            )}
            {geofenceType == GEOFENCE_SHAPE.CIRCLE && (
                <CircleMapper
                    radius={radius}
                    center={center}
                    isEdit
                    updatePoint={setCenter}
                />
            )}
        </GoogleMap>
    ) : (
        <></>
    )
})

export default React.memo(GeofenceMap)
